import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  RefreshCw, 
  Mail, 
  Package, 
  CheckCircle, 
  XCircle, 
  Clock,
  AlertTriangle,
  Eye,
  ChevronLeft,
  ChevronRight,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

const ITEMS_PER_PAGE = 20;

// Color hex to name mapping
const COLOR_NAMES = {
  '#FFFFFF': 'White',
  '#ffffff': 'White',
  '#222222': 'Black',
  '#1F2937': 'Navy',
  '#DC2626': 'Red',
  '#F59E0B': 'Gold',
  '#0EA5E9': 'Turquoise',
  '#16A34A': 'Kelly Green',
};

function StatusBadge({ status, type }) {
  const configs = {
    payment: {
      paid: { color: 'bg-green-500', icon: CheckCircle, text: 'Paid' },
      pending: { color: 'bg-yellow-500', icon: Clock, text: 'Pending' },
      failed: { color: 'bg-red-500', icon: XCircle, text: 'Failed' },
    },
    printful: {
      draft: { color: 'bg-blue-500', icon: Package, text: 'Draft' },
      pending: { color: 'bg-yellow-500', icon: Clock, text: 'Pending' },
      fulfilled: { color: 'bg-green-500', icon: CheckCircle, text: 'Fulfilled' },
      canceled: { color: 'bg-gray-500', icon: XCircle, text: 'Canceled' },
      none: { color: 'bg-red-500', icon: AlertTriangle, text: 'Not Created' },
    },
  };

  const statusKey = status?.toLowerCase() || 'none';
  const config = configs[type]?.[statusKey] || configs[type]?.none || { 
    color: 'bg-gray-400', 
    icon: Clock, 
    text: status || 'Unknown' 
  };
  const Icon = config.icon;

  return (
    <Badge className={`${config.color} text-white flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {config.text}
    </Badge>
  );
}

function OrderRow({ order, onRetryPrintful, onResendEmail, onViewDetails }) {
  const [retrying, setRetrying] = useState(false);
  const [resending, setResending] = useState(false);

  const handleRetryPrintful = async () => {
    setRetrying(true);
    try {
      await onRetryPrintful(order.id);
    } finally {
      setRetrying(false);
    }
  };

  const handleResendEmail = async () => {
    setResending(true);
    try {
      await onResendEmail(order.id);
    } finally {
      setResending(false);
    }
  };

  const colorName = COLOR_NAMES[order.tshirt_color] || order.tshirt_color;
  const createdAt = new Date(order.created_at).toLocaleString();
  const needsPrintful = order.payment_status === 'paid' && !order.printful_order_id;

  return (
    <tr className={`border-b border-zinc-700 hover:bg-zinc-800/50 ${needsPrintful ? 'bg-red-900/20' : ''}`}>
      <td className="px-4 py-3">
        <div className="flex flex-col">
          <span className="font-mono text-sm text-zinc-300">{order.id.slice(0, 8)}...</span>
          <span className="text-xs text-zinc-500">{createdAt}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col">
          <span className="text-sm text-white">{order.full_name}</span>
          <span className="text-xs text-zinc-400">{order.email}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col gap-1">
          <span className="text-sm text-zinc-300">{colorName} / {order.tshirt_size}</span>
          <span className="text-xs text-zinc-500">${order.total_price?.toFixed(2)}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={order.payment_status} type="payment" />
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col gap-1">
          <StatusBadge status={order.printful_order_id ? 'draft' : 'none'} type="printful" />
          {order.printful_order_id && (
            <span className="text-xs text-zinc-500">#{order.printful_order_id}</span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-8 px-2 border-zinc-600 hover:bg-zinc-700"
            onClick={() => onViewDetails(order.id)}
            data-testid={`view-order-${order.id.slice(0, 8)}`}
          >
            <Eye className="w-4 h-4" />
          </Button>
          {order.payment_status === 'paid' && !order.printful_order_id && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 px-2 border-orange-600 text-orange-400 hover:bg-orange-900/30"
              onClick={handleRetryPrintful}
              disabled={retrying}
              data-testid={`retry-printful-${order.id.slice(0, 8)}`}
            >
              {retrying ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
            </Button>
          )}
          {order.payment_status === 'paid' && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 px-2 border-blue-600 text-blue-400 hover:bg-blue-900/30"
              onClick={handleResendEmail}
              disabled={resending}
              data-testid={`resend-email-${order.id.slice(0, 8)}`}
            >
              {resending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function OrderDetails({ orderId, onClose }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printfulStatus, setPrintfulStatus] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const data = await api.getOrderDetails(orderId);
        setOrder(data);
        
        if (data.printful_order_id) {
          try {
            const pfStatus = await api.getPrintfulStatus(orderId);
            setPrintfulStatus(pfStatus);
          } catch (e) {
            // Printful status is optional - order details still render without it
            logger.warn('Failed to load Printful status:', e);
          }
        }
      } catch (err) {
        logger.error('Failed to load order details:', err);
        toast.error('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-6 text-center text-zinc-400">
        Order not found
      </div>
    );
  }

  const colorName = COLOR_NAMES[order.tshirt_color] || order.tshirt_color;
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={onClose}
          className="text-zinc-400 hover:text-white"
          data-testid="back-to-orders"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Orders
        </Button>
        <h2 className="text-xl font-bold text-white">Order Details</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Order Info */}
        <Card className="bg-zinc-800 border-zinc-700">
          <CardHeader>
            <CardTitle className="text-white">Order Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-400">Order ID</span>
              <span className="font-mono text-white">{order.id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Created</span>
              <span className="text-white">{new Date(order.created_at).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Payment Status</span>
              <StatusBadge status={order.payment_status} type="payment" />
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Total Price</span>
              <span className="text-white font-bold">${order.total_price?.toFixed(2)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Customer Info */}
        <Card className="bg-zinc-800 border-zinc-700">
          <CardHeader>
            <CardTitle className="text-white">Customer Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-400">Name</span>
              <span className="text-white">{order.full_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Email</span>
              <span className="text-white">{order.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Phone</span>
              <span className="text-white">{order.phone}</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-zinc-400">Shipping Address</span>
              <span className="text-white text-xs">{order.shipping_address}</span>
            </div>
          </CardContent>
        </Card>

        {/* Product Info */}
        <Card className="bg-zinc-800 border-zinc-700">
          <CardHeader>
            <CardTitle className="text-white">Product Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-400">Color</span>
              <div className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded border border-zinc-600" 
                  style={{ backgroundColor: order.tshirt_color }}
                />
                <span className="text-white">{colorName}</span>
              </div>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Size</span>
              <span className="text-white">{order.tshirt_size}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Quantity</span>
              <span className="text-white">{order.quantity}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Print Placement</span>
              <span className="text-white capitalize">{order.print_placement}</span>
            </div>
          </CardContent>
        </Card>

        {/* Printful Info */}
        <Card className="bg-zinc-800 border-zinc-700">
          <CardHeader>
            <CardTitle className="text-white">Printful Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-400">Printful Order ID</span>
              <span className="text-white font-mono">{order.printful_order_id || 'Not created'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Status</span>
              <StatusBadge status={order.printful_order_id ? (order.printful_status || 'draft') : 'none'} type="printful" />
            </div>
            {printfulStatus?.printful_order && (
              <>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Printful Status</span>
                  <span className="text-white">{printfulStatus.printful_order.status}</span>
                </div>
                {printfulStatus.printful_order.shipping && (
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Shipping Method</span>
                    <span className="text-white">{printfulStatus.printful_order.shipping}</span>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Design Previews */}
      <Card className="bg-zinc-800 border-zinc-700">
        <CardHeader>
          <CardTitle className="text-white">Design Files</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['front', 'back', 'left', 'right'].map((side) => {
              const url = order[`${side}_design_url`];
              return (
                <div key={side} className="flex flex-col items-center gap-2">
                  <span className="text-sm text-zinc-400 capitalize">{side}</span>
                  {url ? (
                    <a 
                      href={`${API_BASE}${url}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <img 
                        src={`${API_BASE}${url}`}
                        alt={`${side} design`}
                        className="w-24 h-32 object-contain bg-zinc-900 rounded border border-zinc-700 hover:border-blue-500 transition-colors"
                      />
                    </a>
                  ) : (
                    <div className="w-24 h-32 bg-zinc-900 rounded border border-zinc-700 flex items-center justify-center">
                      <span className="text-xs text-zinc-600">No design</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AdminDashboard() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [config, setConfig] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [filter, setFilter] = useState('all'); // all, paid, pending, needs_printful

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getOrders(100, 0);
      setOrders(data);
    } catch (err) {
      logger.error('Failed to load orders:', err);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const data = await api.getDebugConfig();
      setConfig(data);
    } catch (err) {
      // Config loading failure is non-critical
      logger.warn('Failed to load service config:', err);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchConfig();
  }, [fetchOrders, fetchConfig]);

  const handleRetryPrintful = async (orderId) => {
    try {
      const result = await api.retryPrintful(orderId);
      if (result.status === 'success') {
        toast.success(`Printful order created: #${result.printful_order_id}`);
        fetchOrders();
      } else {
        toast.error(`Failed: ${result.message || result.status}`);
      }
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    }
  };

  const handleResendEmail = async (orderId) => {
    try {
      const result = await api.resendEmail(orderId);
      if (result.status === 'sent') {
        toast.success(`Email sent to ${result.email}`);
      } else {
        toast.error('Failed to send email');
      }
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    }
  };

  // Filter orders
  const filteredOrders = orders.filter(order => {
    if (filter === 'paid') return order.payment_status === 'paid';
    if (filter === 'pending') return order.payment_status === 'pending';
    if (filter === 'needs_printful') return order.payment_status === 'paid' && !order.printful_order_id;
    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
  const paginatedOrders = filteredOrders.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE);

  // Stats
  const stats = {
    total: orders.length,
    paid: orders.filter(o => o.payment_status === 'paid').length,
    pending: orders.filter(o => o.payment_status === 'pending').length,
    needsPrintful: orders.filter(o => o.payment_status === 'paid' && !o.printful_order_id).length,
    totalRevenue: orders.filter(o => o.payment_status === 'paid').reduce((sum, o) => sum + (o.total_price || 0), 0),
  };

  if (selectedOrderId) {
    return (
      <div className="min-h-screen bg-zinc-900">
        <OrderDetails orderId={selectedOrderId} onClose={() => setSelectedOrderId(null)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">T-Studios Admin</h1>
            <p className="text-zinc-400">Order Management Dashboard</p>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-zinc-400 hover:text-white text-sm flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Back to Shop
            </a>
            <Button
              onClick={() => { fetchOrders(); fetchConfig(); }}
              variant="outline"
              className="border-zinc-600 hover:bg-zinc-700"
              data-testid="refresh-orders"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-zinc-800 border-zinc-700">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-white">{stats.total}</div>
              <div className="text-sm text-zinc-400">Total Orders</div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-800 border-zinc-700">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-400">{stats.paid}</div>
              <div className="text-sm text-zinc-400">Paid</div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-800 border-zinc-700">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-yellow-400">{stats.pending}</div>
              <div className="text-sm text-zinc-400">Pending</div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-800 border-zinc-700">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-red-400">{stats.needsPrintful}</div>
              <div className="text-sm text-zinc-400">Needs Printful</div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-800 border-zinc-700">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-400">${stats.totalRevenue.toFixed(2)}</div>
              <div className="text-sm text-zinc-400">Revenue</div>
            </CardContent>
          </Card>
        </div>

        {/* Config Status */}
        {config && (
          <Card className="bg-zinc-800 border-zinc-700">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <span className="text-zinc-400">Services:</span>
                <Badge className={config.stripe_configured ? 'bg-green-600' : 'bg-red-600'}>
                  Stripe {config.stripe_configured ? '✓' : '✗'}
                </Badge>
                <Badge className={config.printful_configured ? 'bg-green-600' : 'bg-red-600'}>
                  Printful {config.printful_api_test?.startsWith('OK') ? '✓' : '✗'}
                </Badge>
                <Badge className={config.resend_configured ? 'bg-green-600' : 'bg-red-600'}>
                  Resend {config.resend_configured ? '✓' : '✗'}
                </Badge>
                <span className="text-zinc-500">|</span>
                <span className="text-zinc-400">Sender: {config.sender_email}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex gap-2">
          {[
            { value: 'all', label: 'All Orders' },
            { value: 'paid', label: 'Paid' },
            { value: 'pending', label: 'Pending' },
            { value: 'needs_printful', label: 'Needs Printful' },
          ].map(f => (
            <Button
              key={f.value}
              variant={filter === f.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setFilter(f.value); setPage(0); }}
              className={filter === f.value ? 'bg-blue-600' : 'border-zinc-600 hover:bg-zinc-700'}
              data-testid={`filter-${f.value}`}
            >
              {f.label}
              {f.value === 'needs_printful' && stats.needsPrintful > 0 && (
                <span className="ml-1 bg-red-500 text-white rounded-full px-1.5 text-xs">{stats.needsPrintful}</span>
              )}
            </Button>
          ))}
        </div>

        {/* Orders Table */}
        <Card className="bg-zinc-800 border-zinc-700">
          <CardContent className="p-0">
            {loading && (
              <div className="flex items-center justify-center p-12">
                <RefreshCw className="w-8 h-8 animate-spin text-zinc-400" />
              </div>
            )}
            {!loading && filteredOrders.length === 0 && (
              <div className="p-12 text-center text-zinc-400">
                No orders found
              </div>
            )}
            {!loading && filteredOrders.length > 0 && (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="orders-table">
                    <thead className="bg-zinc-900 border-b border-zinc-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Order</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Customer</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Product</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Payment</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Printful</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedOrders.map(order => (
                        <OrderRow
                          key={order.id}
                          order={order}
                          onRetryPrintful={handleRetryPrintful}
                          onResendEmail={handleResendEmail}
                          onViewDetails={setSelectedOrderId}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-700">
                    <span className="text-sm text-zinc-400">
                      Showing {page * ITEMS_PER_PAGE + 1} - {Math.min((page + 1) * ITEMS_PER_PAGE, filteredOrders.length)} of {filteredOrders.length}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                        className="border-zinc-600"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                        disabled={page >= totalPages - 1}
                        className="border-zinc-600"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default AdminDashboard;
